/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { debounce } from "lodash"
import { ResponseWithError } from "types/api"
import { BusinessType, ModifierGroup } from "types/models/menu"

/* eslint-disable no-restricted-globals */
export const sortDiffList = ({
  oldList,
  newList,
  key,
}: {
  oldList: any[]
  newList: any[]
  key?: string
}) =>
  newList.filter((sort, index) => {
    if (key) {
      return sort[key] !== oldList[index][key]
    }
    return sort !== oldList[index]
  })

export const sleep = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function getFirstLetter(string: string) {
  return string?.charAt(0)
}

export function capitalizeFirstLetter(string: string) {
  return string?.charAt(0).toUpperCase() + string?.slice(1)
}

export const numberWithCommas = (x: number) => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

export const isNumeric = (str: string) => {
  if (typeof str !== "string") return false // we only process strings!
  // @ts-ignore
  const isNanStr = isNaN(str)
  return (
    !isNanStr && !isNaN(parseFloat(str)) // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
  ) // ...and ensure strings of whitespace fail
}

export const renderModifierGroupRule = (
  modifierGroup: ModifierGroup,
  textMap: {
    [key: string]: string
  },
) => {
  const { selectionRangeMax, selectionRangeMin } = modifierGroup
  let modifierGroupRule: string = ""

  const requireExactly =
    selectionRangeMax === 1 && selectionRangeMax === selectionRangeMin
  const requireInBetween =
    selectionRangeMin > 0 && selectionRangeMin !== selectionRangeMax
  const optionalWithMax = selectionRangeMin === 0 && selectionRangeMax > 0
  const optionalWithMaxOne = selectionRangeMin === 0 && selectionRangeMax === 1
  const requireMax =
    selectionRangeMin > 0 && selectionRangeMax === selectionRangeMin
  if (requireExactly || requireMax) {
    // Require exactly
    modifierGroupRule = textMap.pickX.replace(/<x>/i, `${selectionRangeMin}`)
  } else if (requireInBetween) {
    // Require inBetween
    modifierGroupRule = textMap.pickXtoY
      .replace(/<x>/i, `${selectionRangeMin}`)
      .replace(/<y>/i, `${selectionRangeMax}`)
  } else if (optionalWithMax) {
    // Optional with maximum selection
    modifierGroupRule = textMap.pickUpToXOptional.replace(
      /<x>/i,
      `${selectionRangeMax}`,
    )
  }

  return {
    modifierGroupRule,
    optionalWithMaxOne,
    requireExactly,
    requireInBetween,
    requireMax,
    optionalWithMax,
  }
}

// check if k-th bit of a given number is set or not.
// first bit is at the 0-th position
export function isKthBitSet(n: number, k: number): boolean {
  // eslint-disable-next-line no-bitwise
  return (n & (1 << k)) > 0
}

/**
 * useful for extracting "min" and "max" validations from BE's error messages (e.g. "min:0,max:2")
 */
export function extractMinMax(
  message: string,
  ignoredCharsRegex = /[()[\] ]/g,
): { min: string; max: string } {
  const { min = "?", max = "?" } = message
    .replace(ignoredCharsRegex, "")
    .split(",")
    .map(e => e.split(":"))
    .reduce<Partial<{ min: string; max: string }>>(
      (acc, curr) => ({
        ...acc,
        [curr[0]]: curr[1],
      }),
      {},
    )
  return { min, max }
}

export function debounceSortHandler<T>(
  onSort: (list: T, ...args: any) => void,
): (list: T, ...args: any) => void {
  return debounce(onSort, 5000, {
    leading: false,
  })
}

export const formHasErrors = (fieldsError: any) => {
  return Object.keys(fieldsError).some(field => fieldsError[field])
}

export const isMartBusinessType = (businessType: number | null) =>
  businessType === BusinessType.Shop

export const getDalErrMessage = (
  errorMessage: string,
  fullErrIfNotExists?: boolean,
) => {
  const msg = errorMessage.split("msg=")
  if (msg.length > 1) {
    return msg[1]
  }
  return fullErrIfNotExists ? errorMessage : ""
}

export const parseJSONSafely = <T = Record<string, unknown>>(
  jsonString: string,
  fallback: T = {} as T,
): T => {
  try {
    return JSON.parse(jsonString) as T
  } catch {
    return fallback
  }
}

export const parseJSONBasedErrorMsg = (jsonString: string): string => {
  const dataAsObj = parseJSONSafely<Record<string, string>>(jsonString)
  const errorMsgArr: string[] = []
  if (dataAsObj) {
    Object.keys(dataAsObj).forEach(key => {
      errorMsgArr.push(dataAsObj[key])
    })
  }
  if (errorMsgArr.length) {
    return errorMsgArr.join("; ")
  }
  return ""
}

export const commonErrorCallback = (response?: ResponseWithError) =>
  response?.message || response?.reason

export const saveFile = (href: string, fileName?: string) => {
  const a = document.createElement("a")
  a.href = href
  // NOTE: fileName doesn't work for cross domain
  if (fileName) {
    a.download = fileName
  }
  document.body.appendChild(a)
  a.click()
  a.remove()
}
